<template>
  <div class="change-password-fi">
    <div
      v-if="!hideOldPassword"
      class="change-password-fi__row"
    >
      <text-input-fi
        id="old-password"
        ref="oldPassword"
        v-model="form.oldPassword"
        name="old-password"
        type="password"
        autocomplete="new-password"
        :rules="rules.required"
        :label="$dict.get('online-Shop.Password.Old-Password')"
        @click:append="form.showOldPassword = !form.showOldPassword"
      />
    </div>

    <div class="change-password-fi__row">
      <text-input-fi
        id="password"
        ref="password"
        v-model="form.password"
        name="password"
        type="password"
        autocomplete="new-password"
        :rules="passwordRules"
        :label="$dict.get('online-Shop.Password.New-Password')"
        @input="formChanged"
        @click:append="form.showPassword = !form.showPassword"
        @blur="activatePasswordValidation(passwordRef)"
      />
    </div>

    <div class="change-password-fi__row change-password-fi__row--password-requirements">
      <password-requirements
        :password="form.password"
        @valid-changed="passwordValidationChanged"
      />
    </div>

    <div class="change-password-fi__row">
      <text-input-fi
        id="password-repeat"
        ref="passwordRepeat"
        v-model="form.passwordRepeat"
        name="password-repeat"
        type="password"
        autocomplete="new-password"
        :rules="enablePasswordRepeatValidation ? checkPasswordRepeat() : []"
        :label="$dict.get('online-Shop.Password.New-Password-Repeat')"
        @click:append="form.showPasswordRepeat = !form.showPasswordRepeat"
        @blur="enablePasswordRepeatValidation = true"
        @input="formChanged"
        @change="passwordRepeatChanged"
        @keyup="passwordRepeatChanged"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, nextTick, reactive, ref, useTemplateRef } from 'vue';

import TextInputFi from '@/components/atoms/text-input-fi/TextInputFi.vue';
import { useDict } from '@/composables/fischerPlugin';
import Validators from '@/utils/validators';

const PasswordRequirements = defineAsyncComponent(
  () => import('@/components/atoms/password-requirements/PasswordRequirements.vue'),
);

defineOptions({ name: 'ChangePasswordFi' });

const props = withDefaults(
  defineProps<{
    hideOldPassword?: boolean;
  }>(),
  {
    hideOldPassword: false,
  },
);

const emit = defineEmits<{
  'form-changed': [form: Record<string, any>];
}>();

const dict = useDict();

const oldPasswordRef = useTemplateRef<any>('oldPassword');
const passwordRef = useTemplateRef<any>('password');

const form = reactive({
  valid: false,
  oldPassword: '',
  password: '',
  showPassword: false,
  showOldPassword: false,
  passwordRepeat: '',
  showPasswordRepeat: false,
});
const enablePasswordValidation = ref(false);
const enablePasswordRepeatValidation = ref(false);
const passwordValid = ref(false);

const rules = computed(() => Validators(dict, null));

const checkPasswordRepeat = () => {
  return [
    (v: any) => !!v || dict.get('online-Shop.Global-Labels.Error-Message-Required'),
    form.password === form.passwordRepeat || dict.get('online-Shop.Password.Match-Check'),
  ];
};

const passwordRules = computed(() => {
  const checkPasswordRule = [
    (v: any) =>
      (passwordValid.value && !!v) || dict.get('online-Shop.Global-Labels.Error-Message-Required'),
  ];
  return enablePasswordValidation.value ? checkPasswordRule : [];
});

const passwordValidationChanged = (value: boolean) => {
  passwordValid.value = value;
};

const passwordRepeatChanged = () => {
  if (enablePasswordRepeatValidation.value) {
    return;
  }
  if (
    form.passwordRepeat !== null &&
    form.passwordRepeat !== undefined &&
    !form.password.startsWith(form.passwordRepeat)
  ) {
    enablePasswordRepeatValidation.value = true;
  }
};

const activatePasswordValidation = async (ref: any) => {
  enablePasswordValidation.value = true;
  await nextTick();
  ref?.validate?.();
};

const formChanged = () => {
  form.valid = Boolean(
    form.password &&
    form.passwordRepeat === form.password &&
    !passwordRef.value?.errorBucket?.length,
  );

  if (!props.hideOldPassword) {
    form.valid = Boolean(
      form.valid && form.oldPassword.length && !oldPasswordRef.value?.errorBucket?.length,
    );
  }

  emit('form-changed', form);
};
</script>

<style src="./change-password-fi" />
